import { useState, useEffect } from 'react';
import {
  customersAuthorizationApiWithoutInterceptor,
  ECustomerAuthorizationEndpoints
} from '../api';
import { useAuth } from './useAuth';
import { IHeaderRequest } from '../EGerdauService/types';

interface CmsBlockState {
  content: string | null;
  loading: boolean;
  error: boolean;
}

export const CMS_CACHE_KEY_PREFIX = 'cms_block_';

const inFlightRequests = new Map<string, Promise<string | null>>();

export function fetchAndCacheCmsBlock(
  blockId: string,
  headers: IHeaderRequest
): Promise<string | null> {
  const cacheKey = `${CMS_CACHE_KEY_PREFIX}${blockId}`;

  const cached = sessionStorage.getItem(cacheKey);
  if (cached) return Promise.resolve(cached);

  if (inFlightRequests.has(blockId))
    return inFlightRequests.get(blockId) as Promise<string | null>;

  const promise = customersAuthorizationApiWithoutInterceptor
    .get(`${ECustomerAuthorizationEndpoints.CMS_BLOCK}/${blockId}`, { headers })
    .then((response) => {
      const content: string | null = response.data?.content ?? null;
      if (content) sessionStorage.setItem(cacheKey, content);
      return content;
    })
    .catch((): null => null)
    .finally(() => inFlightRequests.delete(blockId));

  inFlightRequests.set(blockId, promise);
  return promise;
}

const useCmsBlockService = (
  blockId: string,
  enabled: boolean
): CmsBlockState => {
  const cacheKey = `${CMS_CACHE_KEY_PREFIX}${blockId}`;

  const [state, setState] = useState<CmsBlockState>(() => {
    const cached = sessionStorage.getItem(cacheKey);
    return { content: cached, loading: !cached && enabled, error: false };
  });

  const { getHeaders } = useAuth();

  useEffect(() => {
    if (!enabled || !blockId) return;

    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      setState({ content: cached, loading: false, error: false });
      return;
    }

    let cancelled = false;

    fetchAndCacheCmsBlock(blockId, getHeaders()).then((content) => {
      if (cancelled) return;
      if (content !== null) {
        setState({ content, loading: false, error: false });
      } else {
        setState({ content: null, loading: false, error: true });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [blockId, enabled, cacheKey]);

  return state;
};

export default useCmsBlockService;
